import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getLatestSensorReadings,
  SensorReading,
  SensorTimelineContext,
  OvenTriggerRule,
  OvenAutomationRulesConfig,
  getOvenAutomationRules,
  setOvenAutomationRules,
} from '../api/client';
import SensorRangePicker from '../components/sensors/SensorRangePicker';
import SensorTimelineChart from '../components/sensors/SensorTimelineChart';
import { loadSensorContextCached, loadSensorReadingsCached } from '../utils/sensorTimelineCache';

// ------------------------------------------------------------------
// Oven Automation Triggers
// ------------------------------------------------------------------

const MODBUS_OPERATORS = [
  { value: 'greater_than', label: '> גדול מ' },
  { value: 'greater_than_or_equal', label: '>= גדול או שווה ל' },
  { value: 'less_than', label: '< קטן מ' },
  { value: 'less_than_or_equal', label: '<= קטן או שווה ל' },
  { value: 'equal', label: '== שווה ל' },
  { value: 'not_equal', label: '!= שונה מ' },
  { value: 'increase_by', label: '↗ עולה ב־לפחות' },
  { value: 'decrease_by', label: '↘ יורד ב־לפחות' },
];

const MACHINE_IDS = ['1', '2'];

function OvenTriggerRuleEditor({
  rule,
  onChange,
  registerNames,
}: {
  rule: OvenTriggerRule;
  onChange: (r: OvenTriggerRule) => void;
  registerNames: string[];
}) {
  const isStart = rule.event_type === 'roast_start';
  const isChangeOperator = rule.operator === 'increase_by' || rule.operator === 'decrease_by';
  return (
    <div className={`rounded-xl border-2 p-4 space-y-3 ${isStart ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
      <div className="flex items-center gap-3">
        <span className={`font-bold text-sm ${isStart ? 'text-green-700' : 'text-red-700'}`}>
          {isStart ? '▶ התחלת קלייה' : '■ סיום קלייה'}
        </span>
        <label className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={e => onChange({ ...rule, enabled: e.target.checked })}
            className="accent-gray-500"
          />
          פעיל
        </label>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">שם רגיסטר (Sensor)</label>
        {registerNames.length > 0 ? (
          <select
            value={rule.register_name}
            onChange={e => onChange({ ...rule, register_name: e.target.value })}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          >
            <option value="">בחר רגיסטר</option>
            {registerNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : (
          <input
            type="text"
            value={rule.register_name}
            onChange={e => onChange({ ...rule, register_name: e.target.value })}
            placeholder="Mbus_DrumTemp@Prog16"
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">תנאי</label>
          <select
            value={rule.operator}
            onChange={e => {
              const operator = e.target.value as OvenTriggerRule['operator'];
              onChange({
                ...rule,
                operator,
                consecutive_samples_required:
                  operator === 'increase_by' || operator === 'decrease_by'
                    ? 1
                    : rule.consecutive_samples_required,
              });
            }}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          >
            {MODBUS_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
          </select>
        </div>
        <div className="w-28">
          <label className="block text-xs text-gray-500 mb-1">
            {isChangeOperator ? 'שינוי מינימלי' : 'ערך'}
          </label>
          <input
            type="number"
            value={rule.threshold}
            onChange={e => onChange({ ...rule, threshold: Number(e.target.value) })}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div className="w-20">
          <label className="block text-xs text-gray-500 mb-1">דגימות</label>
          <input
            type="number"
            min={1}
            max={10}
            value={rule.consecutive_samples_required}
            onChange={e => onChange({ ...rule, consecutive_samples_required: Math.max(1, Number(e.target.value)) })}
            title="מספר דגימות רצופות לאישור לפני שיגור אירוע"
            disabled={isChangeOperator}
            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
      </div>
      <p className="text-xs text-gray-400">
        {isChangeOperator
          ? 'השינוי נמדד לעומת דגימת החיישן הקודמת ומופעל מיד.'
          : `דגימות: מינימום ${rule.consecutive_samples_required} דגימות רצופות שמקיימות את התנאי לפני שיגור האירוע (מניעת ספייקים)`}
      </p>
    </div>
  );
}

function OvenAutomationSection({ sensorReadings }: { sensorReadings: SensorReading[] }) {
  const [expanded, setExpanded] = useState(false);
  const [configs, setConfigs] = useState<Record<string, OvenAutomationRulesConfig>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  const registerNames = Array.from(
    new Set(sensorReadings.map(r => r.register_name).filter(Boolean))
  ).sort();

  useEffect(() => {
    if (!expanded) return;
    MACHINE_IDS.forEach(async (id) => {
      try {
        const cfg = await getOvenAutomationRules(id);
        setConfigs(prev => ({ ...prev, [id]: cfg }));
      } catch {
        // backend may return 404 for unconfigured ovens — start with empty
      }
    });
  }, [expanded]);

  const handleRuleChange = (machineId: string, updated: OvenTriggerRule[]) => {
    setConfigs(prev => ({
      ...prev,
      [machineId]: { ...(prev[machineId] ?? { machine_id: machineId, rules: [] }), rules: updated },
    }));
    setSaved(prev => ({ ...prev, [machineId]: false }));
  };

  const handleSave = async (machineId: string) => {
    const cfg = configs[machineId];
    if (!cfg) return;
    setSaving(prev => ({ ...prev, [machineId]: true }));
    try {
      await setOvenAutomationRules(machineId, cfg.rules);
      setSaved(prev => ({ ...prev, [machineId]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [machineId]: false })), 2000);
    } catch (err) {
      console.error('Failed to save automation rules:', err);
    } finally {
      setSaving(prev => ({ ...prev, [machineId]: false }));
    }
  };

  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl border border-espresso-200/60 shadow-soft">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-right"
      >
        <h2 className="text-sm font-semibold text-espresso-700 uppercase tracking-wide">⚙ טריגרים לתנורים (Modbus)</h2>
        <span className="text-espresso-400 text-lg">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {MACHINE_IDS.map(machineId => {
            const cfg = configs[machineId];
            const startRule = cfg?.rules.find(r => r.event_type === 'roast_start') ?? {
              event_type: 'roast_start' as const,
              register_name: 'Mbus_DrumTemp@Prog16',
              operator: 'greater_than',
              threshold: 100,
              consecutive_samples_required: 3,
              enabled: false,
            };
            const endRule = cfg?.rules.find(r => r.event_type === 'roast_end') ?? {
              event_type: 'roast_end' as const,
              register_name: 'Mbus_DrumTemp@Prog16',
              operator: 'less_than',
              threshold: 80,
              consecutive_samples_required: 3,
              enabled: true,
            };

            const updateRule = (updated: OvenTriggerRule) => {
              const current = configs[machineId]?.rules ?? [startRule, endRule];
              const next = current.some(r => r.event_type === updated.event_type)
                ? current.map(r => r.event_type === updated.event_type ? updated : r)
                : [...current, updated];
              handleRuleChange(machineId, next);
            };

            return (
              <div key={machineId} className="space-y-3">
                <h3 className="text-base font-semibold text-espresso-700 border-b border-espresso-200 pb-2">תנור {machineId}</h3>
                <OvenTriggerRuleEditor rule={startRule} onChange={updateRule} registerNames={registerNames} />
                <OvenTriggerRuleEditor rule={endRule} onChange={updateRule} registerNames={registerNames} />
                <button
                  onClick={() => handleSave(machineId)}
                  disabled={saving[machineId]}
                  className={`w-full py-2 rounded-xl font-bold text-sm transition-colors ${
                    saved[machineId]
                      ? 'bg-green-500 text-white'
                      : 'bg-espresso-800 text-cream-100 hover:bg-espresso-700 disabled:opacity-50'
                  }`}
                >
                  {saving[machineId] ? 'שומר...' : saved[machineId] ? '✓ נשמר' : 'שמור טריגרים לתנור ' + machineId}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PRESETS = [
  { label: '5 דק׳', minutes: 5 },
  { label: '15 דק׳', minutes: 15 },
  { label: '30 דק׳', minutes: 30 },
  { label: 'שעה', minutes: 60 },
  { label: '6 שעות', minutes: 360 },
  { label: '24 שעות', minutes: 1440 },
  { label: '48 שעות', minutes: 2880 },
];
const LINE_COLORS = ['#ef4444', '#f97316', '#ca8a04', '#16a34a', '#0891b2', '#4f46e5'];
const AUTO_REFRESH_MS = 30_000;
// Below a minute the 1s bucket has nothing finer to offer.
const MIN_SPAN_MS = 60_000;

function labelFor(reading: SensorReading) {
  return reading.register_name && reading.register_name !== reading.register_address
    ? reading.register_name
    : `Reg ${reading.register_address}`;
}

export default function Sensors() {
  const initialEnd = useRef(new Date());
  const [presetMinutes, setPresetMinutes] = useState<number | null>(60);
  const [range, setRange] = useState<[Date, Date]>([
    new Date(initialEnd.current.getTime() - 60 * 60_000),
    initialEnd.current,
  ]);
  const [visibleRange, setVisibleRange] = useState<[Date, Date]>(range);
  const [zoomHistory, setZoomHistory] = useState<Array<[Date, Date]>>([]);
  const [selection, setSelection] = useState<[number, number] | null>(null);
  const [device, setDevice] = useState('');
  const [knownDevices, setKnownDevices] = useState<string[]>([]);
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [latest, setLatest] = useState<SensorReading[]>([]);
  const [context, setContext] = useState<SensorTimelineContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const requestId = useRef(0);

  const fetchRange = useCallback(async () => {
    const requested = visibleRange;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    const requiredBucketSeconds = Math.max(
      1,
      Math.ceil((requested[1].getTime() - requested[0].getTime()) / 600_000),
    );
    try {
      const [history, timeline, current] = await Promise.all([
        loadSensorReadingsCached(requested[0], requested[1], requiredBucketSeconds, device || undefined),
        loadSensorContextCached(requested[0], requested[1], device || undefined),
        getLatestSensorReadings(device || undefined),
      ]);
      if (id !== requestId.current) return;
      setReadings(history.readings);
      setContext(timeline);
      setLatest(current);
      setKnownDevices(previous => Array.from(new Set([
        ...previous,
        ...history.readings.map(item => item.device_id),
        ...current.map(item => item.device_id),
      ])).filter(Boolean).sort());
      setLastUpdated(new Date());
    } catch (cause: unknown) {
      if (id === requestId.current) setError(cause instanceof Error ? cause.message : 'שגיאה בטעינת נתונים');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [device, visibleRange]);

  const refresh = useCallback(() => {
    if (presetMinutes != null && zoomHistory.length === 0) {
      const end = new Date();
      const requested: [Date, Date] = [
        new Date(end.getTime() - presetMinutes * 60_000),
        end,
      ];
      setRange(requested);
      setVisibleRange(requested);
      return;
    }
    void fetchRange();
  }, [fetchRange, presetMinutes, zoomHistory.length]);

  useEffect(() => { void fetchRange(); }, [fetchRange]);
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(refresh, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [autoRefresh, refresh]);

  const selectPreset = (minutes: number) => {
    const end = new Date();
    const next: [Date, Date] = [new Date(end.getTime() - minutes * 60_000), end];
    setPresetMinutes(minutes);
    setRange(next);
    setVisibleRange(next);
    setZoomHistory([]);
    setSelection(null);
  };

  const applyCustomRange = (next: [Date, Date]) => {
    setPresetMinutes(null);
    setRange(next);
    setVisibleRange(next);
    setZoomHistory([]);
    setSelection(null);
  };

  // Clamps a proposed window inside the committed `range`. Overflow shifts the
  // window back inside rather than shrinking it, so a step near an edge still
  // widens by the full factor; only a span wider than `range` gets truncated.
  const applyWindow = (start: number, end: number) => {
    const low = range[0].getTime();
    const high = range[1].getTime();
    const span = Math.min(end - start, high - low);
    let nextStart = Math.min(Math.max(start, low), high - span);
    if (nextStart < low) nextStart = low;
    const next: [Date, Date] = [new Date(nextStart), new Date(nextStart + span)];
    if (next[0].getTime() === visibleRange[0].getTime() && next[1].getTime() === visibleRange[1].getTime()) return;
    setZoomHistory(previous => [...previous, visibleRange]);
    setVisibleRange(next);
    setSelection(null);
  };

  const zoomTo = (start: number, end: number) => applyWindow(start, end);

  const zoomBy = (factor: number) => {
    const [from, to] = [visibleRange[0].getTime(), visibleRange[1].getTime()];
    const middle = (from + to) / 2;
    // Floor applies to the buttons only; drag-to-zoom keeps its finer precision.
    const span = Math.max((to - from) * factor, MIN_SPAN_MS);
    applyWindow(middle - span / 2, middle + span / 2);
  };

  const panBy = (direction: number) => {
    const [from, to] = [visibleRange[0].getTime(), visibleRange[1].getTime()];
    const step = (to - from) * 0.25 * direction;
    applyWindow(from + step, to + step);
  };

  const atFullRange = visibleRange[0].getTime() <= range[0].getTime() && visibleRange[1].getTime() >= range[1].getTime();
  const atMinSpan = visibleRange[1].getTime() - visibleRange[0].getTime() <= MIN_SPAN_MS;

  const backZoom = () => {
    const previous = zoomHistory[zoomHistory.length - 1];
    if (!previous) return;
    setVisibleRange(previous);
    setZoomHistory(items => items.slice(0, -1));
    setSelection(null);
  };

  const resetZoom = () => {
    setVisibleRange(range);
    setZoomHistory([]);
    setSelection(null);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl">חיישנים</h1>
        {lastUpdated && <span className="text-xs text-espresso-400">עודכן: {lastUpdated.toLocaleTimeString('he-IL')} {loading && '· טוען…'}</span>}
      </div>

      <div className="card relative z-30 overflow-visible p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          {PRESETS.map(preset => (
            <button
              key={preset.minutes}
              onClick={() => selectPreset(preset.minutes)}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium ${presetMinutes === preset.minutes ? 'bg-espresso-800 text-cream-100' : 'bg-espresso-100 text-espresso-700'}`}
            >
              {preset.label}
            </button>
          ))}
          <div className="w-px h-7 bg-espresso-200 mx-1" />
          <SensorRangePicker value={range} onChange={applyCustomRange} />
          <select value={device} onChange={event => setDevice(event.target.value)} className="input w-auto self-end">
            <option value="">כל המכשירים</option>
            {knownDevices.map(id => <option key={id} value={id}>{id}</option>)}
          </select>
          <div className="flex-1" />
          <label className="flex items-center gap-2 text-sm text-espresso-600 self-end pb-2">
            <input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} className="accent-gold-500" />
            רענון חי (30 שנ׳)
          </label>
          <button onClick={refresh} disabled={loading} className="btn-primary self-end">
            {loading ? 'טוען…' : '↻ רענן'}
          </button>
        </div>
        <div className="text-xs text-espresso-400">
          הטווח נשלח ב-UTC: {range[0].toISOString()} — {range[1].toISOString()}
        </div>
      </div>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">⚠ {error}</div>}

      <div className="card relative z-0 p-5">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-espresso-700">ציר זמן חיישנים, תערובות ותמונות</h2>
            <p className="text-xs text-espresso-400 mt-1">לחצו על שם חיישן כדי להציג או להסתיר אותו; גררו על הגרף כדי להתקרב לטווח מדויק</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => panBy(-1)} disabled={atFullRange} title="אחורה בזמן" aria-label="אחורה בזמן" className="btn-secondary px-3 py-1.5 disabled:opacity-40">⟵</button>
            <button onClick={() => zoomBy(2)} disabled={atFullRange} title="הקטן" aria-label="הקטן" className="btn-secondary px-3 py-1.5 disabled:opacity-40">−</button>
            <button onClick={() => zoomBy(0.5)} disabled={atMinSpan} title="הגדל" aria-label="הגדל" className="btn-secondary px-3 py-1.5 disabled:opacity-40">+</button>
            <button onClick={() => panBy(1)} disabled={atFullRange} title="קדימה בזמן" aria-label="קדימה בזמן" className="btn-secondary px-3 py-1.5 disabled:opacity-40">⟶</button>
            <div className="w-px bg-espresso-200 mx-1" />
            <button onClick={backZoom} disabled={!zoomHistory.length} className="btn-secondary px-3 py-1.5 disabled:opacity-40">חזרה</button>
            <button onClick={resetZoom} disabled={!zoomHistory.length} className="btn-secondary px-3 py-1.5 disabled:opacity-40">איפוס זום</button>
          </div>
        </div>
        {loading && readings.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-espresso-400">טוען נתונים…</div>
        ) : (
          <>
            {readings.length === 0 && <div className="text-center text-sm text-espresso-400">אין קריאות חיישנים לתקופה; הקשר התנורים עדיין מוצג</div>}
            <SensorTimelineChart
              readings={readings}
              context={context}
              start={visibleRange[0].getTime()}
              end={visibleRange[1].getTime()}
              selection={selection}
              onSelectionChange={setSelection}
              onZoom={zoomTo}
            />
          </>
        )}
      </div>

      {latest.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-espresso-700 mb-3 uppercase tracking-wide">ערכים אחרונים</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {latest.map((reading, index) => (
              <div key={`${reading.device_id}-${reading.register_address}`} className="card p-4 text-center">
                <div className="w-2 h-2 rounded-full mx-auto mb-2" style={{ background: LINE_COLORS[index % LINE_COLORS.length] }} />
                <div className="text-xs text-espresso-500 truncate" title={labelFor(reading)}>{labelFor(reading)}</div>
                <div className="heading text-2xl mt-1">{Number.isFinite(Number(reading.value)) ? Number(reading.value).toFixed(2) : reading.value}</div>
                <div className="text-xs text-espresso-300 truncate">{reading.device_id}</div>
                <div className="text-[10px] text-espresso-300">{new Date(reading.time).toLocaleTimeString('he-IL')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <OvenAutomationSection sensorReadings={latest} />
    </div>
  );
}
